/**
 * Inspection result colours — same rule on web and worker app.
 *
 * Pass (complete OK)     → green
 * Incomplete / in progress → yellow
 * Fail                   → red
 * Not tested / unknown   → slate (neutral)
 */

export const INSPECTION_RESULT = {
  PASS: 'PASS',
  FAIL: 'FAIL',
  INCOMPLETE: 'INCOMPLETE',
  NOT_TESTED: 'NOT_TESTED',
};

/** Normalize any outcome / status string into the four buckets. */
export function normalizeInspectionResult(raw) {
  const s = String(raw || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  if (!s) return INSPECTION_RESULT.NOT_TESTED;
  if (
    s === 'PASS' ||
    s === 'PASSED' ||
    s === 'OK' ||
    s === 'COMPLETED' ||
    s === 'COMPLETE' ||
    s === 'REPAIRED_PASS' ||
    s === 'SUCCESS'
  ) {
    return INSPECTION_RESULT.PASS;
  }
  if (s === 'FAIL' || s === 'FAILED' || s === 'FAILURE' || s === 'REJECTED' || s === 'DEFECT') {
    return INSPECTION_RESULT.FAIL;
  }
  if (
    s === 'INCOMPLETE' ||
    s === 'IN_PROGRESS' ||
    s === 'PARTIAL' ||
    s === 'STARTED' ||
    s === 'RESTORED' ||
    s === 'AFTER_PAPERWORK' ||
    s === 'REPAIRING'
  ) {
    return INSPECTION_RESULT.INCOMPLETE;
  }
  if (s === 'NOT_TESTED' || s === 'PENDING' || s === 'NONE') {
    return INSPECTION_RESULT.NOT_TESTED;
  }
  return INSPECTION_RESULT.NOT_TESTED;
}

/** Chip / badge: border + bg + text */
export function inspectionResultChipClass(raw) {
  switch (normalizeInspectionResult(raw)) {
    case INSPECTION_RESULT.PASS:
      return 'border-green-500 bg-green-50 text-green-800';
    case INSPECTION_RESULT.FAIL:
      return 'border-red-500 bg-red-50 text-red-800';
    case INSPECTION_RESULT.INCOMPLETE:
      return 'border-yellow-500 bg-yellow-50 text-yellow-900';
    default:
      return 'border-slate-400 bg-white text-slate-700';
  }
}

/** Text-only */
export function inspectionResultTextClass(raw) {
  switch (normalizeInspectionResult(raw)) {
    case INSPECTION_RESULT.PASS:
      return 'text-green-700 font-semibold';
    case INSPECTION_RESULT.FAIL:
      return 'text-red-700 font-semibold';
    case INSPECTION_RESULT.INCOMPLETE:
      return 'text-yellow-800 font-semibold';
    default:
      return 'text-gray-700';
  }
}

/** Solid accent bar / dot */
export function inspectionResultAccentClass(raw) {
  switch (normalizeInspectionResult(raw)) {
    case INSPECTION_RESULT.PASS:
      return 'bg-green-500';
    case INSPECTION_RESULT.FAIL:
      return 'bg-red-500';
    case INSPECTION_RESULT.INCOMPLETE:
      return 'bg-yellow-400';
    default:
      return 'bg-slate-300';
  }
}

/** Soft card background */
export function inspectionResultCardBgClass(raw) {
  switch (normalizeInspectionResult(raw)) {
    case INSPECTION_RESULT.PASS:
      return 'bg-green-50';
    case INSPECTION_RESULT.FAIL:
      return 'bg-red-50';
    case INSPECTION_RESULT.INCOMPLETE:
      return 'bg-yellow-50';
    default:
      return 'bg-white';
  }
}

/** Badge (filled) */
export function inspectionResultBadgeClass(raw) {
  switch (normalizeInspectionResult(raw)) {
    case INSPECTION_RESULT.PASS:
      return 'bg-green-100 text-green-800';
    case INSPECTION_RESULT.FAIL:
      return 'bg-red-100 text-red-800';
    case INSPECTION_RESULT.INCOMPLETE:
      return 'bg-yellow-100 text-yellow-900';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}
